import './style.scss';

export function header_block() {
  const header = document.querySelector('[data-site-header]');
  if (!header || header.dataset.fbsThemeNavigationReady === 'true') return;

  const toggle = header.querySelector('.fbs-theme-menu-toggle');
  const overlay = header.querySelector('.fbs-theme-menu-overlay');
  const navigation = overlay?.querySelector('.fbs-theme-menu-navigation');

  if (!toggle || !overlay || !navigation) return;

  header.dataset.fbsThemeNavigationReady = 'true';

  // The Site Editor uses a native Navigation block so labels and destinations
  // can be changed visually. Normalize its rendered markup back to the classes
  // used by the existing frontend animation/styles. This also keeps legacy
  // saved template parts working during the transition.
  const menuList = navigation.querySelector('.wp-block-navigation__container, .fbs-theme-menu-list');
  menuList?.classList.add('fbs-theme-menu-list');

  const menuLinks = [...navigation.querySelectorAll('a[href]')];
  menuLinks.forEach((link) => {
    link.classList.add('fbs-theme-menu-link');
    link.closest('li')?.classList.add('fbs-theme-menu-item');
  });

  const normalizePath = (value) => {
    try {
      const url = new URL(value, window.location.origin);
      const path = url.pathname.replace(/\/+$/, '');
      return path || '/';
    } catch (error) {
      return '';
    }
  };

  let currentLink = menuLinks.find(
      (link) => ['page', 'true'].includes(link.getAttribute('aria-current')) || link.closest('.current-menu-item'),
  );

  if (!currentLink) {
    const currentPath = normalizePath(window.location.href);
    currentLink = menuLinks.find((link) => normalizePath(link.href) === currentPath);

    if (currentLink) {
      currentLink.setAttribute('aria-current', 'page');
    }
  }

  const bodyClasses = document.body.classList;

  if (!currentLink && (bodyClasses.contains('single-work') || bodyClasses.contains('tax-work_category'))) {
    currentLink = navigation.querySelector('.fbs-theme-menu-item--work a[href]');
  }

  if (
    !currentLink
    && (
      bodyClasses.contains('single-post')
      || bodyClasses.contains('category')
      || bodyClasses.contains('tag')
      || bodyClasses.contains('blog')
    )
  ) {
    currentLink = navigation.querySelector('.fbs-theme-menu-item--news a[href]');
  }

  currentLink?.classList.add('is-current');

  overlay.setAttribute('aria-hidden', 'true');
  overlay.setAttribute('inert', '');

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  let isOpen = false;
  const root = document.documentElement;

  const setScrollbarCompensation = () => {
    const scrollbarWidth = Math.max(0, window.innerWidth - root.clientWidth);
    root.style.setProperty('--fbs-theme-scrollbar-compensation', `${scrollbarWidth}px`);
  };

  const clearScrollbarCompensation = () => {
    root.style.removeProperty('--fbs-theme-scrollbar-compensation');
  };

  // Keep text hover/current states synchronized without decorative markers.
  let highlighted = null;
  // Set around openMenu()'s programmatic focus() so that one focus event does
  // not drag the highlight with it. focus() dispatches synchronously, so this
  // is only ever true for the duration of that single call.
  let suppressHighlightFocus = false;

  menuList?.classList.add('is-highlight-managed');

  // `pointer` marks a highlight the reader asked for by pointing at or tabbing
  // to the link, as opposed to the resting one the current page gets for free.
  // Only the former is worth scaling up.
  const setHighlight = (link, {pointer = false} = {}) => {
    if (highlighted && highlighted !== link) {
      highlighted.classList.remove('is-highlighted', 'is-highlighted--pointer');
    }

    highlighted = link || null;

    if (!highlighted) {
      return;
    }

    highlighted.classList.add('is-highlighted');
    highlighted.classList.toggle('is-highlighted--pointer', pointer);
  };

  // Where the highlight belongs when nothing is being pointed at: on the
  // current page's link, or nowhere at all when this page is not in the menu.
  const restHighlight = () => setHighlight(currentLink || null);

  menuLinks.forEach((link) => {
    link.addEventListener('mouseenter', () => setHighlight(link, {pointer: true}));
    link.addEventListener('focus', () => {
      if (suppressHighlightFocus) return;
      setHighlight(link, {pointer: true});
    });
  });

  // On the list: leaving a link for the gap below it is still "in the list", so
  // the highlight stays put and nothing flashes. It only goes back to the
  // current page once the pointer is off the links altogether.
  menuList?.addEventListener('mouseleave', () => restHighlight());
  menuList?.addEventListener('focusout', (event) => {
    if (!menuList.contains(event.relatedTarget)) {
      restHighlight();
    }
  });

  const getFocusableElements = () => [
    toggle,
    ...[...overlay.querySelectorAll(focusableSelector)].filter(
        (element) => element.getClientRects().length > 0,
    ),
  ];

  const openMenu = () => {
    if (isOpen) return;

    setScrollbarCompensation();
    isOpen = true;
    header.classList.add('is-navigation-open');
    document.documentElement.classList.add('fbs-theme-navigation-open');
    document.body.classList.add('fbs-theme-navigation-open');
    toggle.setAttribute('aria-expanded', 'true');
    toggle.setAttribute('aria-label', 'Close menu');
    overlay.setAttribute('aria-hidden', 'false');
    overlay.removeAttribute('inert');

    const firstLink = currentLink || menuLinks[0];

    suppressHighlightFocus = true;
    firstLink?.focus({preventScroll: true});
    suppressHighlightFocus = false;

    restHighlight();
  };

  // `instant`: link clicks close with no reverse animation at all (the page
  // is navigating away anyway) — only the X/Escape close plays the reverse
  // of the open transition.
  const closeMenu = ({restoreFocus = true, instant = false} = {}) => {
    if (!isOpen) return;

    isOpen = false;

    if (instant) {
      header.classList.add('is-navigation-closing-instant');
    }

    header.classList.remove('is-navigation-open');
    document.documentElement.classList.remove('fbs-theme-navigation-open');
    document.body.classList.remove('fbs-theme-navigation-open');
    clearScrollbarCompensation();
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', 'Open menu');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('inert', '');

    if (instant) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          header.classList.remove('is-navigation-closing-instant');
        });
      });
    }

    if (restoreFocus) {
      toggle.focus({preventScroll: true});
    }
  };

  const desktopNavigation = window.matchMedia('(min-width: 1024px)');
  desktopNavigation.addEventListener('change', (event) => {
    if (event.matches) closeMenu({restoreFocus: false, instant: true});
  });

  toggle.addEventListener('click', () => {
    if (isOpen) {
      closeMenu();
      return;
    }

    openMenu();
  });

  menuLinks.forEach((link) => {
    link.addEventListener('click', () => closeMenu({restoreFocus: false, instant: true}));
  });

  document.addEventListener('keydown', (event) => {
    if (!isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeMenu();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusableElements = getFocusableElements();
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (!firstElement || !lastElement) return;

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  });
}
